<template>
  <div
    v-for="handle in activeHandles"
    :key="handle"
    class="absolute z-10 w-2 h-2 bg-info border-1 border-solid border-info"
    :class="{
      '-top-1 -left-1 cursor-nw-resize': handle === 'top-left',
      '-top-1 left-1/2 -translate-x-1/2 cursor-n-resize': handle === 'top-center',
      '-top-1 -right-1 cursor-ne-resize': handle === 'top-right',
      '-left-1 top-1/2 -translate-y-1/2 cursor-w-resize': handle === 'center-left',
      '-right-1 top-1/2 -translate-y-1/2 cursor-e-resize': handle === 'center-right',
      '-bottom-1 -left-1 cursor-sw-resize': handle === 'bottom-left',
      '-bottom-1 left-1/2 -translate-x-1/2 cursor-s-resize': handle === 'bottom-center',
      '-bottom-1 -right-1 cursor-se-resize': handle === 'bottom-right',
    }"
    @mousedown.stop="startResize($event, handle)"
  />
</template>

<script setup lang="ts">
import { ref, type CSSProperties, computed } from 'vue'

export type ResizeHandlePosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'center-left'
  | 'center-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right'

export interface ResizeHandle {
  position: ResizeHandlePosition
  cursor: string
}

const props = withDefaults(
  defineProps<{
    modelValue: CSSProperties
    handles?: ResizeHandlePosition[]
    maintainAspectRatio?: boolean
  }>(),
  {
    handles: () => [],
    maintainAspectRatio: false,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: Record<string, any>]
  'resize-start': []
  'resize-end': []
}>()

const allHandles: ResizeHandlePosition[] = [
  'top-left',
  'top-center',
  'top-right',
  'center-left',
  'center-right',
  'bottom-left',
  'bottom-center',
  'bottom-right',
]

const activeHandles = computed(() =>
  props.handles?.length ? allHandles?.filter((handle) => props.handles?.includes(handle)) : allHandles,
)

const isResizing = ref(false)
const initialPosition = ref({ top: 0, left: 0, x: 0, y: 0 })
const initialSize = ref({ width: 0, height: 0 })
const aspectRatio = ref(1)
let activeHandle = ''

function disableSelection() {
  document.body.style.userSelect = 'none'
}

function enableSelection() {
  document.body.style.userSelect = ''
}

function startResize(event: MouseEvent, position: string) {
  isResizing.value = true
  activeHandle = position
  disableSelection()
  emit('resize-start')

  initialPosition.value = {
    x: event.clientX,
    y: event.clientY,
    top: props.modelValue?.top
      ? parseInt(props.modelValue.top.toString())
      : (event.target as HTMLElement).parentElement?.parentElement?.offsetTop || 0,
    left: props.modelValue?.left
      ? parseInt(props.modelValue.left.toString())
      : (event.target as HTMLElement).parentElement?.parentElement?.offsetLeft || 0,
  }

  const element = event.target as HTMLElement
  const parent = element.parentElement as HTMLElement

  initialSize.value = {
    width: parent.offsetWidth,
    height: parent.offsetHeight,
  }

  aspectRatio.value = initialSize.value.width / initialSize.value.height

  document.addEventListener('mousemove', handleResize)
  document.addEventListener('mouseup', stopResize)
}

function handleResize(event: MouseEvent) {
  if (!isResizing.value) return

  const dx = event.clientX - initialPosition.value.x
  const dy = event.clientY - initialPosition.value.y

  const newSize = calculateNewSize(dx, dy, activeHandle, initialSize.value)

  const width = newSize.width ? parseInt(newSize.width) : 0
  const height = newSize.height ? parseInt(newSize.height) : 0
  const hasInvalidDimensions = width < 0 || height < 0

  if (hasInvalidDimensions) return

  emit('update:modelValue', { ...props.modelValue, ...newSize })
}

function calculateNewSize(dx: number, dy: number, position: string, initial: { width: number; height: number }) {
  const newSize: { width?: string; height?: string; left?: string; top?: string } = {}

  if (props.maintainAspectRatio) {
    switch (position) {
      case 'top-left': {
        // Move towards bottom-right (anchor point is bottom-right)
        const newWidth = initial.width - dx
        const newHeight = newWidth / aspectRatio.value
        const heightDiff = initial.height - newHeight
        newSize.width = `${newWidth}px`
        newSize.height = `${newHeight}px`
        newSize.left = `${initialPosition.value.left + dx}px`
        newSize.top = `${initialPosition.value.top + heightDiff}px`
        break
      }
      case 'top-right': {
        // Move towards bottom-left (anchor point is bottom-left)
        const newWidth = initial.width + dx
        const newHeight = newWidth / aspectRatio.value
        const heightDiff = initial.height - newHeight
        newSize.width = `${newWidth}px`
        newSize.height = `${newHeight}px`
        newSize.top = `${initialPosition.value.top + heightDiff}px`
        break
      }
      case 'bottom-left': {
        // Move towards top-right
        const newWidth = initial.width - dx
        const newHeight = newWidth / aspectRatio.value
        newSize.width = `${newWidth}px`
        newSize.height = `${newHeight}px`
        newSize.left = `${initialPosition.value.left + dx}px`
        break
      }
      case 'bottom-right': {
        // Move towards top-left
        const newWidth = initial.width + dx
        newSize.width = `${newWidth}px`
        newSize.height = `${newWidth / aspectRatio.value}px`
        break
      }
      case 'center-right': {
        // Expand/contract from center
        const newWidth = initial.width + dx
        const newHeight = newWidth / aspectRatio.value
        const heightDiff = (newHeight - initial.height) / 2
        newSize.width = `${newWidth}px`
        newSize.height = `${newHeight}px`
        newSize.top = `${initialPosition.value.top - heightDiff}px`
        break
      }
      case 'center-left': {
        // Expand/contract from center
        const newWidth = initial.width - dx
        const newHeight = newWidth / aspectRatio.value
        const heightDiff = (newHeight - initial.height) / 2
        newSize.width = `${newWidth}px`
        newSize.height = `${newHeight}px`
        newSize.left = `${initialPosition.value.left + dx}px`
        newSize.top = `${initialPosition.value.top - heightDiff}px`
        break
      }
      case 'top-center': {
        // Expand/contract vertically from center
        const newHeight = initial.height - dy
        const newWidth = newHeight * aspectRatio.value
        const widthDiff = (newWidth - initial.width) / 2
        newSize.width = `${newWidth}px`
        newSize.height = `${newHeight}px`
        newSize.left = `${initialPosition.value.left - widthDiff}px`
        newSize.top = `${initialPosition.value.top + dy}px`
        break
      }
      case 'bottom-center': {
        // Expand/contract horizontally from center
        const newHeight = initial.height + dy
        const newWidth = newHeight * aspectRatio.value
        const widthDiff = (newWidth - initial.width) / 2
        newSize.width = `${newWidth}px`
        newSize.height = `${newHeight}px`
        newSize.left = `${initialPosition.value.left - widthDiff}px`
        break
      }
    }
  } else {
    // Non-aspect ratio preserving logic remains unchanged
    switch (position) {
      case 'center-right':
      case 'top-right':
      case 'bottom-right':
        newSize.width = `${initial.width + dx}px`
        break
      case 'center-left':
      case 'top-left':
      case 'bottom-left':
        newSize.width = `${initial.width - dx}px`
        newSize.left = `${initialPosition.value.left + dx}px`
        break
    }

    switch (position) {
      case 'top-left':
      case 'top-right':
        newSize.height = `${initial.height - dy}px`
        newSize.top = `${initialPosition.value.top + dy}px`
        break
      case 'bottom-center':
      case 'bottom-left':
      case 'bottom-right':
        newSize.height = `${initial.height + dy}px`
        break
    }
  }

  return newSize
}

function stopResize() {
  isResizing.value = false
  enableSelection()
  document.removeEventListener('mousemove', handleResize)
  document.removeEventListener('mouseup', stopResize)
  emit('resize-end')
}
</script>
